import { getSessionToken } from '@common/helpers'
import { resolveWorkspaceProjectAndSyncLastUsed } from '@common/utils/resolveWorkspaceProjectAndSyncLastUsed'
import { runInitialAppDataParallelBatch } from '@common/utils/runInitialAppDataParallelBatch'

import { ensureOnboardingComplete } from '@apps/onboarding/stores/onboarding.thunks'
import { getUserInvitations } from '@modules/user'
import { initializeUserRegionalData as initializeUserRegionalDataHelper } from '@modules/user/helpers/initalize-user-regional-data'
import { useUserStore } from '@modules/user/stores/user.store'
import { getCurrentUser as fetchCurrentUserFromApi } from '@modules/user/user'
import type { InitializeRegionalInput, UserProfile } from '@modules/user/user.interface'
import { useLayoutStore } from '@stores/layout/layout.store'

import { AppInitPhase } from './app-init.const'
import { useAppInitStore } from './app-init.store'

export type AppInitAuth0User = {
  nickname?: string | null
  name?: string | null
  picture?: string | null
  email?: string | null
  email_verified?: boolean
  sub?: string
}

export type SyncAppInitializationInput = {
  auth0Loading: boolean
  auth0User: AppInitAuth0User | null | undefined
  isVerified: boolean
  isAcceptInvitation: boolean
}

function toRegionalAuth0Input(auth0User: AppInitAuth0User): InitializeRegionalInput['auth0User'] {
  return {
    sub: auth0User.sub,
    email: auth0User.email,
    name: auth0User.name ?? undefined,
    nickname: auth0User.nickname ?? undefined,
  }
}

function buildUserProfile(auth0User: AppInitAuth0User): UserProfile {
  return {
    nickname: auth0User.nickname ?? null,
    name: auth0User.name ?? null,
    picture: auth0User.picture ?? null,
    email: auth0User.email ?? null,
    email_verified: auth0User.email_verified,
    sub: auth0User.sub,
  }
}

async function fetchAndSetCurrentUser(profile: UserProfile): Promise<void> {
  const { setState } = useUserStore.getState()

  try {
    setState({ loading: true, error: null })
    const user = await fetchCurrentUserFromApi()
    setState({
      currentUser: user
        ? {
            ...user,
            profile,
          }
        : null,
    })
  } catch {
    setState({ currentUser: null })
    throw new Error('Failed to load current user')
  } finally {
    setState({ loading: false })
  }
}

async function initializeUserRegionalDataForAuth0(auth0User: AppInitAuth0User): Promise<void> {
  const { setCurrentUser, setState } = useUserStore.getState()

  try {
    setState({ loading: true, error: null })
    const latestCurrentUser = useUserStore.getState().currentUser

    const { regionalData, regionalIdentity } = await initializeUserRegionalDataHelper({
      auth0User: toRegionalAuth0Input(auth0User),
      currentUser: latestCurrentUser,
    })

    const dataChanged =
      JSON.stringify(latestCurrentUser?.regionalData) !== JSON.stringify(regionalData) ||
      JSON.stringify(latestCurrentUser?.regionalIdentity) !== JSON.stringify(regionalIdentity)

    if (dataChanged) {
      setCurrentUser({
        ...(latestCurrentUser ?? {}),
        regionalData: regionalData ?? undefined,
        regionalIdentity: regionalIdentity ?? undefined,
      })
    }
  } finally {
    setState({ loading: false })
  }
}

async function loadAppData(auth0User: AppInitAuth0User): Promise<void> {
  const profile = buildUserProfile(auth0User)

  await fetchAndSetCurrentUser(profile)
  await getSessionToken()

  const { workspace } = await runInitialAppDataParallelBatch(() => initializeUserRegionalDataForAuth0(auth0User))

  if (workspace) {
    await resolveWorkspaceProjectAndSyncLastUsed(workspace)
  }

  useUserStore.getState().setInvitations(await getUserInvitations())
}

async function runAppInitializationPipeline(auth0User: AppInitAuth0User, skipOnboarding: boolean): Promise<void> {
  const appInit = useAppInitStore.getState()
  const layout = useLayoutStore.getState()

  appInit.markPipelineStarted()
  appInit.setPhase(AppInitPhase.LoadingAppData)
  appInit.setError(null)
  layout.startLoader()

  try {
    await loadAppData(auth0User)
    appInit.setHasInitialized(true)

    if (!skipOnboarding) {
      appInit.setPhase(AppInitPhase.RunningOnboarding)
      await ensureOnboardingComplete()
    }

    appInit.setPhase(AppInitPhase.Ready)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to initialize app data'
    appInit.setError(message)
    appInit.setPhase(AppInitPhase.Failed)
    console.error('[app-init]', err)
  } finally {
    layout.clearFullScreenLoader()
  }
}

export async function syncAppInitialization(input: SyncAppInitializationInput): Promise<void> {
  const { auth0Loading, auth0User, isVerified, isAcceptInvitation } = input
  const appInit = useAppInitStore.getState()
  const layout = useLayoutStore.getState()

  if (auth0Loading) {
    return
  }

  if (!auth0User) {
    appInit.reset()
    layout.clearFullScreenLoader()
    return
  }

  const canInitializeAppData = isVerified || isAcceptInvitation

  if (!canInitializeAppData) {
    appInit.markBlockedUnverified()
    layout.clearFullScreenLoader()
    return
  }

  if (appInit.pipelineStarted) {
    return
  }

  await runAppInitializationPipeline(auth0User, isAcceptInvitation)
}
